import React, { useState } from 'react';
import { FaTimes, FaEye, FaUpload } from 'react-icons/fa';
import FileManager from './FileManager';

const FileInput = ({ 
  label, 
  value, 
  onChange, 
  allowedTypes = ['JPG', 'JPEG', 'PNG', 'GIF'], 
  showPreview = true,
  placeholder = "Choose file",
  required = false,
  error = null,
  className = "",
  multiple = false
}) => {
  const [isFileManagerOpen, setIsFileManagerOpen] = useState(false);

  const handleFileSelect = (selectedFile) => {
    if (multiple) {
      // Handle multiple files
      const urls = Array.isArray(selectedFile) 
        ? selectedFile.map(file => file.url || file) 
        : [selectedFile.url || selectedFile];
      onChange(urls);
    } else {
      // Handle single file
      if (Array.isArray(selectedFile)) {
        // If array is passed but we need single file
        const fileObj = selectedFile[0];
        onChange(fileObj?.url || fileObj);
      } else {
        // Direct file object handling
        // Check if it's a structured object from FileManager
        if (selectedFile && typeof selectedFile === 'object') {
          // First check if it has url property directly
          if (selectedFile.url) {
            onChange(selectedFile.url);
          } 
          // Then check if it has file or fmFile property with url
          else if (selectedFile.file && selectedFile.file.url) {
            onChange(selectedFile.file.url);
          }
          else if (selectedFile.fmFile && selectedFile.fmFile.url) {
            onChange(selectedFile.fmFile.url);
          }
          // Fallback to the object itself if it's a string
          else {
            onChange(selectedFile);
          }
        } else {
          // Direct value (string URL)
          onChange(selectedFile);
        }
      }
    }
    setIsFileManagerOpen(false);
  };

  const handleRemoveFile = () => {
    onChange(multiple ? [] : '');
  };

  const hasValue = multiple ? (Array.isArray(value) && value.length > 0) : Boolean(value);
  const displayValue = multiple ? 
    (Array.isArray(value) ? `${value.length} file(s) selected` : 'No files selected') :
    (value || '');

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="space-y-3">
        {/* File selector button */}
        <div 
          onClick={() => setIsFileManagerOpen(true)}
          className="flex items-center justify-between p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <FaUpload className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-700">
                {hasValue ? displayValue : placeholder}
              </p>
              <p className="text-xs text-gray-500">
                Allowed types: {allowedTypes.join(', ')}
              </p>
            </div>
          </div>
          
          {hasValue && (
            <div className="flex items-center space-x-2">
              {!multiple && value && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(value, '_blank');
                  }}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Preview file"
                >
                  <FaEye size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Remove file"
              >
                <FaTimes size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Preview for single image */}
        {showPreview && !multiple && value && (
          <div className="relative inline-block">
            <img
              src={value}
              alt="Preview"
              className="max-w-xs max-h-32 object-cover rounded-lg border"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <button
              type="button"
              onClick={handleRemoveFile}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Remove image"
            >
              <FaTimes size={12} />
            </button>
          </div>
        )}

        {/* Preview for multiple files */}
        {showPreview && multiple && Array.isArray(value) && value.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {value.map((fileUrl, index) => (
              <div key={index} className="relative">
                <img
                  src={fileUrl}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-20 object-cover rounded-lg border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const newValue = value.filter((_, i) => i !== index);
                    onChange(newValue);
                  }}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  title="Remove image"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}

      {/* File Manager Modal */}
      <FileManager
        isOpen={isFileManagerOpen}
        onClose={() => setIsFileManagerOpen(false)}
        onSelect={handleFileSelect}
        accept={allowedTypes}
        showPreview={true}
        title={`Select ${multiple ? 'Files' : 'File'}`}
      />
    </div>
  );
};

export default FileInput;
